import React, { FC, Fragment, useContext, useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from 'i18next';
import Spinner from "components/Spinner/Spinner";
import Banner from "components/Banner/Banner";
import PromoDetailRender from "./components/PromoDetail";
import IntroDetailRender from "./components/IntroDetail";
import PolicyRender from "./components/Policy";
import SidebarRender from "./components/Sidebar";
import MobileSidebar from "./components/MobileSidebar";
import { commonAxiosPostRequest } from "utils/request";
import { getPureArray } from "utils/util";
import { Result } from "antd";
import ButtonPrimary from "shared/Button/ButtonPrimary";

export interface ListingProductPageProps {
	className?: string;
	isPreviewMode?: boolean;
}

export type TSkuTypes = {
	grounpId: string;
	propertyId: string;
	propertyName: string;
	typeName: string;
}

export interface ISku {
	id: string;
	productId: string;
	name: string;
	nameEn: string;
	nameHk: string;
	stocks: number;
	sellingPrice: number;
	pictureUrl: string;
	skusTypes: TSkuTypes[];
	attribute: ({ [key: string]: any });
	attributeText?: string;
	quantity?: number;
	totalPrice?: string | number;
};

export type TSkuAttribute = {
	id: string;
	name: string;
	nameEn: string;
	nameHk: string;
	propertyList: ({
		id: string;
		name: string;
		selected?: boolean;
		disabled?: boolean;
	})[];
};
export interface IProductInfo {
	id: string;
	name: string;
	nameEn: string;
	nameHk: string;
	intro: string;
	introEn: string;
	introHk: string;
	pictureUrls: string[];
	detailContent: string;
	detailContentEn: string;
	detailContentHk: string;
	sellingPrice: number;
	skuAttributeList: TSkuAttribute[];
	skus: ISku[];
	salesPolicy: string;
}

const ProductDetailPage: FC<ListingProductPageProps> = ({ className = "", isPreviewMode }) => {
	const { id: productId } = useParams() as any;
	const { t } = useTranslation();
	const history = useHistory();
	const { language } = i18n;

	// 当前商品信息
	const [productInfo, setProductInfo] = useState<IProductInfo | null>(null);
	// 与 PC 侧栏共用的当前规格，移动端底栏展示同一总价
	const [currentSkuInfo, setCurrentSkuInfo] = useState<ISku | null>(null);
	// 页面加载状态：0/1/2
	const [loadingState, setLoadingState] = useState<0 | 1 | 2>(0);

	useEffect(() => {
		let cancelled = false;
		const loadProductDetail = async () => {
			setLoadingState(1);
			setProductInfo(null);
			setCurrentSkuInfo(null);
			const [, response] = await commonAxiosPostRequest<IProductInfo, {}>('/universal_mall/api/app/product/detail', {
				productId,
			});
			if (cancelled) {
				return;
			}
			setLoadingState(2);
			setProductInfo(response?.id ? response : null);
		};
		loadProductDetail();
		return () => {
			cancelled = true;
		};
	}, [productId]);

	return (
		<div
			className={`nc-ListingStayDetailPage flex w-full flex-col items-center justify-center pb-24 lg:pb-0 ${className}`}
			data-nc-id="ListingStayDetailPage"
		>
			{loadingState === 1 && <Spinner className="pt-24 pb-24 lg:py-32" />}

			{loadingState === 2 && !productInfo ? (
				<Result
					status="404"
					title={t("product.not_found")}
					subTitle={t("product.not_found_desc")}
					extra={<ButtonPrimary href="/">{t("common.backHome")}</ButtonPrimary>}
				/>
			) : null}

			{loadingState === 2 && productInfo ? (
				<>
					<header className="container mt-4 w-full min-w-0 rounded-md sm:mt-6 sm:rounded-xl 2xl:px-14">
						<Banner imageArr={productInfo?.pictureUrls || []} />
					</header>
					<main className="container mt-6 flex w-full min-w-0 sm:mt-11">
						<div className="w-full min-w-0 space-y-6 sm:space-y-8 lg:w-3/5 lg:space-y-10 lg:pr-10 xl:w-2/3">
							{/* 產品介绍 */}
							<IntroDetailRender productInfo={productInfo}></IntroDetailRender>
							{/* 產品推廣 */}
							<PromoDetailRender productInfo={productInfo}></PromoDetailRender>
							{/* 條款及細則 */}
							<PolicyRender productInfo={productInfo}></PolicyRender>
						</div>
						{/* SIDEBAR */}
						<div className="hidden lg:block flex-grow">
							<div className="sticky top-24">
								{
									<SidebarRender key={productInfo.id} productInfo={productInfo} onChange={setCurrentSkuInfo}></SidebarRender>
								}
							</div>
						</div>
					</main>
					{/* 移动端添加购物车模块 */}
					<MobileSidebar key={productInfo?.id || "empty"} productInfo={productInfo} currentSkuInfo={currentSkuInfo} onSkuChange={setCurrentSkuInfo} />
				</>
			) : null}
		</div>
	);
};

export default ProductDetailPage;
